import { z } from "zod";
/**
 * **The coin history — A72 source 4, Ivy §5.2.**
 *
 * ── The two things this payload is LOCKED about ─────────────────────────────
 *
 * 1. **No balance.** Ivy §5.1 / A72 item 1: the screen reads the one-truth
 *    `balanceCoins` from `GET /players/me`. A total is never summed from this
 *    list either — Ivy §13's "never" column.
 * 2. **No room ids, ever** (Ivy §5.2, S3 §20.2, A72 item 3). `context` is a
 *    *name pair*, joined server-side from `rooms.game_type_id` (A71) and the
 *    room's config mode. **If the join yields nothing, `context` is `null`** —
 *    never an id, never a truncated id, never a `#`-prefixed one. The row then
 *    renders its reason alone.
 */
/**
 * `coin_transactions.reason`, as the client sees it — one COPY-SLOT per value
 * (Ivy §14, seven labels).
 *
 * Spelled here rather than imported from `@wr/db`: `@wr/contracts` never depends
 * on Drizzle (architecture §9), and `@wr/ledger`'s own tests already bind the
 * database enum to this list's order. A value the server can emit and this
 * cannot name would fail response serialisation in CI, which is the intended
 * failure mode.
 */
export const COIN_TRANSACTION_REASONS = [
    "room.entry",
    "room.refund",
    "room.prize",
    "shop.purchase",
    "admin.grant",
    "purchase.topup",
    "grant.conversion",
    /**
     * **DR-2 (T-305), and it arrives here with a COPY-SLOT still owed.**
     *
     * `grant.daily` joined `coin_transaction_reason` in migration 0024. This list
     * is not optional bookkeeping: the profile route serialises its response
     * through `transactionsResponseSchema`, so a reason the database can hold and
     * this enum cannot name is a **500 on the coin history** for any player who
     * has one — *"a value the server can emit and this cannot name would fail
     * response serialisation"*, exactly as the note above promises. Nothing can
     * write such a row before DR-3, and the value is landed here now so that DR-3
     * cannot be the ticket that discovers it.
     *
     * **What is still owed, and by whom:** the eighth COPY-SLOT
     * (`profile.tx.reason.grant.daily`) is Theo's to write and Nora's to pass —
     * a schema ticket does not author player-visible copy. Until it lands the row
     * would render its raw key, which is ugly but is not a broken screen; the
     * i18n coverage test in `apps/web` names this exemption explicitly and fails
     * the moment the label appears, so the exemption cannot outlive the copy.
     * Ivy §14's parallel-noun rule applies to the eighth label as it did to the
     * seven.
     */
    "grant.daily",
];
export const coinTransactionReasonSchema = z.enum(COIN_TRANSACTION_REASONS);
/**
 * The rendered context — "Word Search · 1v1".
 *
 * Names, never ids, and never assembled by the client: the layout appends the
 * separator, the string does not carry it (Ivy §14's note to Theo).
 */
export const transactionContextSchema = z.object({
    gameTypeName: z.string(),
    modeName: z.string(),
});
export const transactionItemSchema = z.object({
    /**
     * `coin_transactions.id` as a **string**. It is a `bigserial`, and the client's
     * only uses for it are a React key and comparing two rows for identity — both
     * of which a string serves without ever inviting arithmetic on a number that
     * may one day exceed `Number.MAX_SAFE_INTEGER`.
     */
    id: z.string(),
    at: z.string(),
    reason: coinTransactionReasonSchema,
    /** Signed, never 0. The **sign is the signal** — Ivy §5.2 puts no colour on it. */
    amountDelta: z.number().int(),
    context: transactionContextSchema.nullable(),
});
/** Ivy §5.2: 20 rows a page, cursor-based, newest first, never infinite scroll. */
export const TRANSACTIONS_PAGE_SIZE = 20;
export const TRANSACTIONS_MAX_PAGE_SIZE = 50;
/**
 * `GET /api/v1/players/me/transactions?cursor=&limit=20`.
 *
 * The cursor is `coin_transactions.id` descending (bigserial, monotone — A72
 * item 3), passed back verbatim as an opaque string. Keyset, not OFFSET: a
 * ledger grows at the head and OFFSET paging over a growing list silently
 * repeats and skips rows.
 */
export const transactionsQuerySchema = z.object({
    cursor: z
        .string()
        .regex(/^\d{1,19}$/, "cursor must be a transaction id")
        .optional(),
    limit: z.coerce.number().int().min(1).max(TRANSACTIONS_MAX_PAGE_SIZE).optional(),
});
export const transactionsResponseSchema = z.object({
    items: z.array(transactionItemSchema),
    /** `null` when the last page has been read — the "Show more" button is then absent, not disabled. */
    nextCursor: z.string().nullable(),
});
//# sourceMappingURL=transactions.js.map